import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { type SQLiteService, getSQLite } from "../_interop/sqlite";
import { env } from "../env";
import { getDocumentDotMomentPath, getDocumentRequestHistoryDbPath } from "../paths/paths.internal";
import { $lastQueryKeyForPage, pageKey } from "./page-last-status.atom";
import { $queryState, createEmptyQueryState } from "./query-state.atom";
import { requestExecutionStateKey } from "./request-execution-state-key";
import {
	type RequestPageHistoryEntry,
	type RequestPageHistoryRow,
	deserializeRequestPageHistoryRow,
	serializeRequestPageHistoryState,
} from "./request-page-history.types";
import { $httpRequestPage } from "./request-page.atom";
import type { HttpRequestPage, QueryError, QueryResponse, QueryTiming } from "./request-page.types";
import { requestPageKeyForDocsRoute } from "./requestPageKey";

const logger = new Logger("request-page-history");

const REQUEST_HISTORY_MAX_ROWS = 100;
const REQUEST_HISTORY_PAGE_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const REQUEST_HISTORY_TABLE_ORDER_BY = "created_at DESC, id DESC";

type RequestPageHistoryParams = {
	documentId: string;
	pageId: string;
};

type RecordRequestPageHistorySnapshotInput = RequestPageHistoryParams & {
	requestPage: HttpRequestPage;
	response?: QueryResponse;
	error?: QueryError;
	timing?: QueryTiming;
};

type SelectRequestPageHistoryEntryInput = RequestPageHistoryParams & {
	historyEntryId: number;
};

type RenameRequestPageHistoryTableInput = {
	documentId: string;
	fromPageId: string;
	toPageId: string;
};

const requestHistoryWriteQueue = new Map<string, Promise<void>>();

function quoteSqliteIdentifier(identifier: string): string {
	return `"${identifier.replaceAll('"', '""')}"`;
}

function assertValidRequestHistoryPageId(pageId: string): string {
	if (!REQUEST_HISTORY_PAGE_ID_PATTERN.test(pageId)) {
		throw new Error(`Invalid pageId for request history table: ${pageId}`);
	}

	return pageId;
}

function tableNameForPageId(pageId: string): string {
	return quoteSqliteIdentifier(assertValidRequestHistoryPageId(pageId));
}

function requestHistoryQueueKey(params: RequestPageHistoryParams): string {
	return `${params.documentId}:${params.pageId}`;
}

function requestHistoryQueueKeysForRename(
	input: RenameRequestPageHistoryTableInput
): RequestPageHistoryParams[] {
	return [
		{ documentId: input.documentId, pageId: input.fromPageId },
		{ documentId: input.documentId, pageId: input.toPageId },
	];
}

async function ensureRequestHistoryDb(documentId: string): Promise<string | null> {
	if (!env.isDesktopApp) {
		return null;
	}

	const dotMomentPath = await getDocumentDotMomentPath(documentId);
	const { exists, mkdir } = await import("@tauri-apps/plugin-fs");
	if (!(await exists(dotMomentPath))) {
		await mkdir(dotMomentPath, { recursive: true });
	}

	return getDocumentRequestHistoryDbPath(documentId);
}

async function withRequestHistoryDb<T>(
	documentId: string,
	callback: (params: { databasePath: string; sqlite: SQLiteService }) => Promise<T>
): Promise<T | null> {
	const sqlite = getSQLite();
	if (!sqlite) {
		return null;
	}

	const databasePath = await ensureRequestHistoryDb(documentId);
	if (!databasePath) {
		return null;
	}

	return callback({ databasePath, sqlite });
}

async function ensureRequestHistoryTable(params: RequestPageHistoryParams): Promise<void> {
	const tableName = tableNameForPageId(params.pageId);
	await withRequestHistoryDb(params.documentId, async ({ databasePath, sqlite }) => {
		await sqlite.executeRaw(
			`CREATE TABLE IF NOT EXISTS ${tableName} (
				id INTEGER PRIMARY KEY AUTOINCREMENT,
				created_at INTEGER NOT NULL,
				schemaVersion INTEGER NOT NULL,
				kind TEXT NOT NULL,
				pageType TEXT NOT NULL,
				request TEXT NOT NULL,
				ui TEXT,
				response TEXT,
				error TEXT,
				timing TEXT
			)`,
			[],
			{ databasePath }
		);
	});
}

async function requestHistoryTableExists(params: RequestPageHistoryParams): Promise<boolean> {
	const pageId = assertValidRequestHistoryPageId(params.pageId);
	const result = await withRequestHistoryDb(
		params.documentId,
		async ({ databasePath, sqlite }) => {
			const rows = await sqlite.queryRaw<{ name: string }[]>(
				"SELECT name FROM sqlite_master WHERE type = ? AND name = ? LIMIT 1",
				["table", pageId],
				{ databasePath }
			);

			return rows.length > 0;
		}
	);

	return result ?? false;
}

async function loadRequestPageHistoryEntries(
	params: RequestPageHistoryParams
): Promise<RequestPageHistoryEntry[]> {
	const exists = await requestHistoryTableExists(params);
	if (!exists) {
		return [];
	}

	const tableName = tableNameForPageId(params.pageId);
	const result = await withRequestHistoryDb(
		params.documentId,
		async ({ databasePath, sqlite }) => {
			const rows = await sqlite.queryRaw<RequestPageHistoryRow[]>(
				`SELECT
				id,
				created_at,
				schemaVersion,
				kind,
				pageType,
				request,
				ui,
				response,
				error,
				timing
			FROM ${tableName}
			ORDER BY ${REQUEST_HISTORY_TABLE_ORDER_BY}`,
				[],
				{ databasePath }
			);

			return rows
				.map((row) => deserializeRequestPageHistoryRow(row))
				.filter((entry): entry is RequestPageHistoryEntry => entry !== null);
		}
	);

	return result ?? [];
}

async function trimRequestHistoryTable(
	sqlite: SQLiteService,
	databasePath: string,
	tableName: string
): Promise<void> {
	await sqlite.executeRaw(
		`DELETE FROM ${tableName}
		WHERE id NOT IN (
			SELECT id
			FROM ${tableName}
			ORDER BY ${REQUEST_HISTORY_TABLE_ORDER_BY}
			LIMIT ${REQUEST_HISTORY_MAX_ROWS}
		)`,
		[],
		{ databasePath }
	);
}

function normalizeRequestHistoryQueueKeys(queueKeys: string[]): string[] {
	return [...new Set(queueKeys)].sort();
}

async function enqueueRequestHistoryWriteForQueueKeys<T>(
	queueKeys: string[],
	callback: () => Promise<T>
): Promise<T> {
	const normalizedQueueKeys = normalizeRequestHistoryQueueKeys(queueKeys);
	const previousWrites = normalizedQueueKeys.map(
		(queueKey) => requestHistoryWriteQueue.get(queueKey) ?? Promise.resolve()
	);
	const next = Promise.all(
		previousWrites.map((previousWrite) =>
			previousWrite.then(
				async () => undefined,
				async () => undefined
			)
		)
	).then(callback);
	const nextSettled = next.then(
		() => undefined,
		() => undefined
	);

	for (const queueKey of normalizedQueueKeys) {
		requestHistoryWriteQueue.set(queueKey, nextSettled);
	}

	return next;
}

async function enqueueRequestHistoryWrite<T>(
	params: RequestPageHistoryParams,
	callback: () => Promise<T>
): Promise<T> {
	return enqueueRequestHistoryWriteForQueueKeys([requestHistoryQueueKey(params)], callback);
}

async function recordRequestPageHistorySnapshot(
	input: RecordRequestPageHistorySnapshotInput
): Promise<number | null> {
	return enqueueRequestHistoryWrite(input, async () => {
		await ensureRequestHistoryTable(input);

		const tableName = tableNameForPageId(input.pageId);
		const result = await withRequestHistoryDb(
			input.documentId,
			async ({ databasePath, sqlite }) => {
				const createdAt = input.timing?.endedAt ?? Date.now();
				const payload = serializeRequestPageHistoryState(
					{
						...input.requestPage,
						response: input.response,
						error: input.error,
						timing: input.timing,
					},
					createdAt
				);

				await sqlite.executeRaw(
					`INSERT INTO ${tableName} (
					created_at,
					schemaVersion,
					kind,
					pageType,
					request,
					ui,
					response,
					error,
					timing
				) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
					[
						payload.created_at,
						payload.schemaVersion,
						payload.kind,
						payload.pageType,
						payload.request,
						payload.ui,
						payload.response,
						payload.error,
						payload.timing,
					],
					{ databasePath }
				);

				await trimRequestHistoryTable(sqlite, databasePath, tableName);

				const latest = await sqlite.queryRaw<{ id: number }[]>(
					`SELECT id
				FROM ${tableName}
				ORDER BY id DESC
				LIMIT 1`,
					[],
					{ databasePath }
				);

				return latest[0]?.id ?? null;
			}
		);

		return result ?? null;
	});
}

async function dropRequestPageHistoryTable(params: RequestPageHistoryParams): Promise<void> {
	await enqueueRequestHistoryWrite(params, async () => {
		const exists = await requestHistoryTableExists(params);
		if (!exists) {
			return;
		}

		const tableName = tableNameForPageId(params.pageId);
		await withRequestHistoryDb(params.documentId, async ({ databasePath, sqlite }) => {
			await sqlite.executeRaw(`DROP TABLE IF EXISTS ${tableName}`, [], { databasePath });
		});
	});
}

async function renameRequestPageHistoryTable(
	input: RenameRequestPageHistoryTableInput
): Promise<void> {
	if (input.fromPageId === input.toPageId) {
		return;
	}

	await enqueueRequestHistoryWriteForQueueKeys(
		requestHistoryQueueKeysForRename(input).map((params) => requestHistoryQueueKey(params)),
		async () => {
			const fromExists = await requestHistoryTableExists({
				documentId: input.documentId,
				pageId: input.fromPageId,
			});
			if (!fromExists) {
				return;
			}

			const fromTableName = tableNameForPageId(input.fromPageId);
			const toTableName = tableNameForPageId(input.toPageId);
			const toExists = await requestHistoryTableExists({
				documentId: input.documentId,
				pageId: input.toPageId,
			});
			await withRequestHistoryDb(input.documentId, async ({ databasePath, sqlite }) => {
				if (toExists) {
					await sqlite.executeRaw(
						`INSERT INTO ${toTableName} (
							created_at,
							schemaVersion,
							kind,
							pageType,
							request,
							ui,
							response,
							error,
							timing
						)
						SELECT
							created_at,
							schemaVersion,
							kind,
							pageType,
							request,
							ui,
							response,
							error,
							timing
						FROM ${fromTableName}
						ORDER BY created_at ASC, id ASC`,
						[],
						{ databasePath }
					);
					await trimRequestHistoryTable(sqlite, databasePath, toTableName);
					await sqlite.executeRaw(`DROP TABLE IF EXISTS ${fromTableName}`, [], {
						databasePath,
					});
					return;
				}

				await sqlite.executeRaw(
					`ALTER TABLE ${fromTableName} RENAME TO ${toTableName}`,
					[],
					{ databasePath }
				);
			});
		}
	);
}

const $requestPageHistoryRefresh = atomFamily((params: RequestPageHistoryParams) => {
	const refreshAtom = atom(0);
	refreshAtom.debugLabel = `requestPageHistoryRefresh/${params.documentId}/${params.pageId}`;
	refreshAtom.debugPrivate = true;
	return refreshAtom;
}, isEqual);

export const $selectedRequestPageHistoryId = atomFamily((params: RequestPageHistoryParams) => {
	const selectedAtom = atom(null as number | null);
	selectedAtom.debugLabel = `selectedRequestPageHistoryId/${params.documentId}/${params.pageId}`;
	selectedAtom.debugPrivate = true;
	return selectedAtom;
}, isEqual);

export const $requestPageHistory = atomFamily((params: RequestPageHistoryParams) => {
	const historyAtom = atom(async (get) => {
		get($requestPageHistoryRefresh(params));
		return loadRequestPageHistoryEntries(params);
	});
	historyAtom.debugLabel = `requestPageHistory/${params.documentId}/${params.pageId}`;
	return historyAtom;
}, isEqual);

export const $recordRequestPageHistorySnapshot = atom(
	null,
	async (_get, set, input: RecordRequestPageHistorySnapshotInput) => {
		const params = { documentId: input.documentId, pageId: input.pageId };
		const historyEntryId = await recordRequestPageHistorySnapshot(input);
		set($requestPageHistoryRefresh(params), (current) => current + 1);
		set($selectedRequestPageHistoryId(params), historyEntryId);
	}
);
$recordRequestPageHistorySnapshot.debugLabel = "recordRequestPageHistorySnapshot";

export function runRequestPageHistoryWriteBestEffort(
	write: () => unknown,
	params: RequestPageHistoryParams,
	onError?: (error: unknown) => void
): void {
	try {
		const result = write();
		void Promise.resolve(result).catch((error) => {
			if (onError) {
				onError(error);
				return;
			}

			logger.error("Failed to record request page history snapshot", {
				documentId: params.documentId,
				pageId: params.pageId,
				error,
			});
		});
	} catch (error) {
		if (onError) {
			onError(error);
			return;
		}

		logger.error("Failed to record request page history snapshot", {
			documentId: params.documentId,
			pageId: params.pageId,
			error,
		});
	}
}

export const $selectRequestPageHistoryEntry = atom(
	null,
	async (get, set, input: SelectRequestPageHistoryEntryInput) => {
		const params = { documentId: input.documentId, pageId: input.pageId };
		const entries = await get($requestPageHistory(params));
		const selectedEntry = entries.find((entry) => entry.id === input.historyEntryId);
		if (!selectedEntry) {
			logger.warn("Request page history entry not found", input);
			return;
		}

		const { response, error, timing, ...requestPage } = selectedEntry.state;
		const requestPageKey = requestPageKeyForDocsRoute(input);
		const queryStateKey = requestExecutionStateKey(input.documentId, input.pageId);
		const timestamp = timing?.endedAt ?? selectedEntry.createdAt;

		try {
			await set($httpRequestPage(requestPageKey), {
				type: "set",
				updater: requestPage,
			});
		} catch (error) {
			logger.error("Failed to restore request page history entry", {
				documentId: input.documentId,
				pageId: input.pageId,
				historyEntryId: input.historyEntryId,
				error,
			});
			return;
		}

		set($lastQueryKeyForPage(pageKey(input.documentId, input.pageId)), queryStateKey);
		set($queryState(queryStateKey), {
			...createEmptyQueryState(),
			data: response,
			error: error ?? undefined,
			dataUpdatedAt: response ? timestamp : undefined,
			errorUpdatedAt: error ? timestamp : undefined,
			lastAccessedAt: Date.now(),
			timing,
		});
		set($selectedRequestPageHistoryId(params), selectedEntry.id);
	}
);
$selectRequestPageHistoryEntry.debugLabel = "selectRequestPageHistoryEntry";

export const $dropRequestPageHistory = atom(
	null,
	async (_get, set, input: RequestPageHistoryParams) => {
		await dropRequestPageHistoryTable(input);
		set($requestPageHistoryRefresh(input), (current) => current + 1);
		set($selectedRequestPageHistoryId(input), null);
	}
);
$dropRequestPageHistory.debugLabel = "dropRequestPageHistory";

export const $renameRequestPageHistory = atom(
	null,
	async (_get, set, input: RenameRequestPageHistoryTableInput) => {
		await renameRequestPageHistoryTable(input);
		set(
			$requestPageHistoryRefresh({ documentId: input.documentId, pageId: input.fromPageId }),
			(current) => current + 1
		);
		set(
			$requestPageHistoryRefresh({ documentId: input.documentId, pageId: input.toPageId }),
			(current) => current + 1
		);
		set(
			$selectedRequestPageHistoryId({
				documentId: input.documentId,
				pageId: input.toPageId,
			}),
			null
		);
	}
);
$renameRequestPageHistory.debugLabel = "renameRequestPageHistory";
